export type PlayerProfileField =
  | "primary_flaw"
  | "primary_focus"
  | "play_frequency"
  | "tour_focus"
  | "scoring_goal";

export type SurveyQuestions = SurveyQuestion[];

export type SurveyQuestion = {
  questionId: number;
  playerProfileField: PlayerProfileField;
  questionText: string;
  options: Array<SurveyQuestionOption>;
};

export type SurveyQuestionOption = {
  value: string;
  label: string;
  thumbnail?: string;
};

export type SurveyAnswers = Record<PlayerProfileField, string | null>;

const DRIVER_THUMBNAIL = "/wp-content/uploads/2025/04/driver.png";
const FAIRWAYWOODS_THUMBNAIL = "/wp-content/uploads/2025/04/fairwaywoods.png";
const IRONS_THUMBNAIL = "/wp-content/uploads/2025/04/irons.png";
const WEDGES_THUMBNAIL = "/wp-content/uploads/2025/04/wedges.png";
const CHIPPING_THUMBNAIL = "/wp-content/uploads/2025/04/chipping.png";
const PUTTING_THUMBNAIL = "/wp-content/uploads/2025/04/putting.png";
const BUNKERPLAY_THUMBNAIL = "/wp-content/uploads/2025/04/bunkerplay.png";
const MENTALGAME_THUMBNAIL = "/wp-content/uploads/2025/04/mentalgame.png";
const GAINDISTANCE_THUMBNAIL = "/wp-content/uploads/2025/04/gaindistance.png";
const SLICE_THUMBNAIL = "/wp-content/uploads/2025/04/slice.png";
const HOOK_THUMBNAIL = "/wp-content/uploads/2025/04/hook.png";
const FAT_THUMBNAIL = "/wp-content/uploads/2025/04/fat.png";
const THIN_THUMBNAIL = "/wp-content/uploads/2025/04/thin.png";
const FATTHIN_THUMBNAIL = "/wp-content/uploads/2025/04/fatthin.png";
const DISTANCECONTROL_THUMBNAIL =
  "/wp-content/uploads/2025/04/distancecontrol.png";
const DIRECTIONCONTROL_THUMBNAIL =
  "/wp-content/uploads/2025/04/directioncontrol.png";
const MISSRIGHT_THUMBNAIL = "/wp-content/uploads/2025/04/missright.png";
const MISSLEFT_THUMBNAIL = "/wp-content/uploads/2025/04/missleft.png";
const SHORTPUTTS_THUMBNAIL = "/wp-content/uploads/2025/04/shortputts.png";
const LAGPUTTING_THUMBNAIL = "/wp-content/uploads/2025/04/lagputting.png";

const QUESTIONS: SurveyQuestions = [
  {
    questionId: 0,
    playerProfileField: "primary_focus",
    questionText:
      "What part of your game are you most interested in improving?",
    options: [
      {
        value: "driver",
        label: "Driver",
        thumbnail: DRIVER_THUMBNAIL,
      },
      {
        value: "fairway-woods-hybrid",
        label: "Fairway Woods",
        thumbnail: FAIRWAYWOODS_THUMBNAIL,
      },
      {
        value: "iron-play",
        label: "Irons",
        thumbnail: IRONS_THUMBNAIL,
      },
      {
        value: "wedges",
        label: "Wedges",
        thumbnail: WEDGES_THUMBNAIL,
      },
      {
        value: "chipping-pitching",
        label: "Chipping / Pitching",
        thumbnail: CHIPPING_THUMBNAIL,
      },
      {
        value: "putting",
        label: "Putting",
        thumbnail: PUTTING_THUMBNAIL,
      },
      {
        value: "bunker-play",
        label: "Bunker Play",
        thumbnail: BUNKERPLAY_THUMBNAIL,
      },
      {
        value: "mental-game-course-mgmt",
        label: "Mental Game",
        thumbnail: MENTALGAME_THUMBNAIL,
      },
      {
        value: "gaining-distance",
        label: "Gain Distance",
        thumbnail: GAINDISTANCE_THUMBNAIL,
      },
    ],
  },
  {
    questionId: 1,
    playerProfileField: "primary_flaw",
    questionText: "When you do hit a bad shot, what is your ball flight miss?",
    options: [
      {
        value: "slice",
        label: "Slice",
        thumbnail: SLICE_THUMBNAIL,
      },
      {
        value: "hook",
        label: "Hook",
        thumbnail: HOOK_THUMBNAIL,
      },
    ],
  },
  {
    questionId: 2,
    playerProfileField: "primary_flaw",
    questionText: "When you do hit a bad shot, what is your ball flight miss?",
    options: [
      {
        value: "fat-contact",
        label: "Fat Shots",
        thumbnail: FAT_THUMBNAIL,
      },
      {
        value: "thin-contact",
        label: "Thin Shots",
        thumbnail: THIN_THUMBNAIL,
      },
    ],
  },
  {
    questionId: 3,
    playerProfileField: "primary_flaw",
    questionText: "What is your typical miss?",
    options: [
      {
        value: "fat-contact",
        label: "Fat Shots",
        thumbnail: FAT_THUMBNAIL,
      },
      {
        value: "thin-contact",
        label: "Thin Shots",
        thumbnail: THIN_THUMBNAIL,
      },
      {
        value: "fat-and-thin-contact",
        label: "Fat & Thin Shots",
        thumbnail: FATTHIN_THUMBNAIL,
      },
    ],
  },
  {
    questionId: 4,
    playerProfileField: "primary_flaw",
    questionText:
      "What is your biggest issue with your 30-100 yard wedge shots?",
    options: [
      {
        value: "fat-contact",
        label: "Fat Shots",
        thumbnail: FAT_THUMBNAIL,
      },
      {
        value: "thin-contact",
        label: "Thin Shots",
        thumbnail: THIN_THUMBNAIL,
      },
      {
        value: "fat-and-thin-contact",
        label: "Fat & Thin Shots",
        thumbnail: FATTHIN_THUMBNAIL,
      },
      {
        value: "distance-control",
        label: "Distance Control",
        thumbnail: DISTANCECONTROL_THUMBNAIL,
      },
    ],
  },
  {
    questionId: 5,
    playerProfileField: "primary_flaw",
    questionText: "When you hit a poor chip, what typically happens?",
    options: [
      {
        value: "fat-contact",
        label: "Fat Contact",
        thumbnail: FAT_THUMBNAIL,
      },
      {
        value: "thin-contact",
        label: "Thin Contact",
        thumbnail: THIN_THUMBNAIL,
      },
      {
        value: "fat-and-thin-contact",
        label: "Fat & Thin Contact",
        thumbnail: FATTHIN_THUMBNAIL,
      },
    ],
  },
  {
    questionId: 6,
    playerProfileField: "primary_flaw",
    questionText: "What is your number one challenge with putting?",
    options: [
      {
        value: "missing-right-putting",
        label: "Right Miss",
        thumbnail: MISSRIGHT_THUMBNAIL,
      },
      {
        value: "missing-left-putting",
        label: "Left Miss",
        thumbnail: MISSLEFT_THUMBNAIL,
      },
      {
        value: "short-putts-putting",
        label: "Short Putts",
        thumbnail: SHORTPUTTS_THUMBNAIL,
      },
      {
        value: "lag-putting",
        label: "Lag Putting",
        thumbnail: LAGPUTTING_THUMBNAIL,
      },
      {
        value: "direction-control-putting",
        label: "Direction Control",
        thumbnail: DIRECTIONCONTROL_THUMBNAIL,
      },
    ],
  },
  {
    questionId: 7,
    playerProfileField: "scoring_goal",
    questionText: "What is your average score?",
    options: [
      {
        value: "scratch-plus",
        label: "Below 70",
      },
      {
        value: "break-80",
        label: "70 - 79",
      },
      {
        value: "break-90",
        label: "80 - 89",
      },
      {
        value: "break-100",
        label: "90 - 99",
      },
      {
        value: "bogey-golfer-1",
        label: "100 - 109",
      },
      {
        value: "bogey-golfer-2",
        label: "110 - 119",
      },
      {
        value: "bogey-golfer-3",
        label: "120+",
      },
    ],
  },
];

// This defines the flow of the survey
export const getNextQuestion = (
  currentId: number,
  currentAnswer: string
): number => {
  if (currentId === 0) {
    switch (currentAnswer) {
      case "driver":
        return 1;
      case "fairway-woods-hybrid":
        return 2;
      case "iron-play":
        return 3;
      case "wedges":
        return 4;
      case "chipping-pitching":
        return 5;
      case "putting":
        return 6;
      default:
        return 7;
    }
  }
  if ([1, 2, 3, 4, 5, 6].includes(currentId)) {
    return 7;
  }
  if (currentId === 7) {
    return -1; // end of survey
  }
  return -1;
};

export default QUESTIONS;
